import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild}    from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {ApDynformsComponent}                                          from '../../../ap-dynforms/ap-dynforms.component';
import {
  ApDynformsConfigFieldset
}                                                                     from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {IApPermissionData}                                            from '../../../ap-interface';
import {ApPermissions}                                                from 'invoker-transport';
import {
  ApDynformsConfigTextbox
}                                                                     from '../../../ap-dynforms/config/ap-dynforms-config-textbox';
import {LoginStore}                                                   from '../../../stores/login/login.store';
import {MapViewStore}                                                 from '../../../stores/layout/mapview.store';
import {LanguageStore}                                                from '../../../stores/translation/language.store';
import {map}                                                          from 'rxjs/operators';
import {
  ApDynformsConfigComboBox
}                                                                     from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynformsValidator}                                          from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                                                   from '@angular/forms';
import {NotifyStore}                                                  from '../../../stores/dialog/notify.store';
import {UserStore}                                                    from '../../../stores/base-data/user.store';
import {ObjectFactory}                                                from 'ts-tooling';
import IUser = Data.Authentication.IUser;
import ILanguage = Data.Language.ILanguage;
import ApValidationLevel = Data.Api.Validation.ApValidationLevel;
import IApValidationResult = Data.Api.Validation.IApValidationResult;

type UserProfileSaveState = {
  isUserProfileChanged: boolean;
  isUserProfileSaving: boolean;
  isLanguageChanged: boolean;
};

/**
 * Component for the edit userprofile
 * As per ticket APV49-1463 user profile edit is temporary disabled
 */
@Component({
  selector: 'ap-edit-userprofile',
  template: `
    <ap-dynforms [caption]="'Global_User_Profile' | translate"
                 [loading$]="loading$"
                 [fieldsets]="formConfig"
                 [headerIcon]="'ap-icon-settings'">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_user_profile_edit_save"
                type="button"
                class="k-button k-primary ap-form-button-right"
                [disabled]="(dynForm.validChanges | async) !== true || (this.disabledButton.asObservable() | async)"
                (click)="onSubmit()">
          {{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>`
})
export class ApUserProfileEditComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  public formConfig: ApDynformsConfigFieldset[] = [];
  public loading$: Observable<boolean> = of(true);
  public caption: string;
  public disabledButton: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  protected ApPermissions = ApPermissions;
  protected apPermissionData: IApPermissionData;

  private _user: IUser;
  private _language: ILanguage;
  private _saveState: UserProfileSaveState | undefined;
  private _changeLanguageWarningKey = 'UserProfile__Change_Language_Warning';
  private _subscriptions: Subscription[] = [];

  constructor(private userStore: UserStore,
              private loginStore: LoginStore,
              private notifyStore: NotifyStore,
              private mapViewStore: MapViewStore,
              private languageStore: LanguageStore) {
  }

  public ngOnInit(): void {
    this.loading$ = this._getLoading();
    this.mapViewStore.hideMapView();
    this._saveState = undefined;
    this._user = ObjectFactory.Copy(this.loginStore.User);
    this._language = ObjectFactory.Copy(this.languageStore.SelectedLanguage);
    this.formConfig = this._getFormFieldSets();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(combineLatest([
      this.userStore.Listen(x => x.saving),
      this.userStore.Listen(x => x.saved)
    ]).subscribe(([saving, _]) => {
      this._onDatabaseUpdate(saving);
    }));
    this._subscriptions.push(this.dynForm.initialized.subscribe((): void => {
      this._subscriptions.push(this.dynForm.form.valueChanges.subscribe(form => {
        this._onFormValuesChanges(form);
      }));
    }));
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(d => d.unsubscribe());
    const warningNotification = this._getWarningNotification();
    if (warningNotification) {
      this.notifyStore.removeMessage(warningNotification);
    }
  }

  public onSubmit(): void {
    this.disabledButton.next(true);
    this._saveState = {
      isUserProfileChanged: false,
      isUserProfileSaving: false,
      isLanguageChanged: false
    };
    if (this._isUserDataChanged(this._user, this.dynForm.form.value)) {
      this._saveState.isUserProfileChanged = true;
    }
    if (this._isLanguageChanged(this.languageStore.SelectedLanguage, this.dynForm.form.value)) {
      this._saveState.isLanguageChanged = true;
    }
    if (this._saveState.isUserProfileChanged) {
      this.userStore.updateUserFullName(
        this.loginStore.UserId,
        this.dynForm.form.value['Forename'],
        this.dynForm.form.value['Surname']
      );
    } else {
      this._afterDatabaseOperation();
      this.disabledButton.next(false);
    }
  }

  private _getLoading(): Observable<boolean> {
    return combineLatest([
      this.languageStore.Listen(x => x.loading),
      this.loginStore.Listen(x => x.login.loading)
    ]).pipe(
      map(x => x.some(y => y))
    );
  }

  private _getFormFieldSets(): ApDynformsConfigFieldset[] {
    return [
      new ApDynformsConfigFieldset({
        key: 'user_profile_fieldset',
        config: [
          new ApDynformsConfigTextbox({
            key: 'Forename',
            label: 'Global__Forename',
            value: this._user.Forename,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigTextbox({
            key: 'Surname',
            label: 'Global__Surname',
            value: this._user.Surname,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigTextbox({
            key: 'EMail',
            label: 'Global__Email',
            value: this._user.EMail,
            disabled: true
          }),
          new ApDynformsConfigComboBox({
            key: 'Language',
            label: 'Language',
            valueField: 'Key',
            textField: 'LanguageName',
            value: this._language,
            options: this.languageStore.Listen(x => x.data),
            valuePrimitive: false
          })
        ]
      })
    ];
  }

  private _onDatabaseUpdate(saving: boolean): void {
    if (!this._saveState || !this._saveState.isUserProfileChanged || !this.disabledButton.getValue()) {
      return;
    }
    if (this._saveState.isUserProfileSaving) {
      this._afterDatabaseOperation();
      this.disabledButton.next(false);
      return;
    }
    this._saveState.isUserProfileSaving = saving;
  }

  private _afterDatabaseOperation(): void {
    if (this._saveState?.isLanguageChanged) {
      const selectedLanguage = this.dynForm.form.value['Language'] as ILanguage;
      this.languageStore.SelectLanguage(selectedLanguage);
      this.languageStore.saveSelectedLanguageToLocalStorage();
      if (!this._saveState.isUserProfileChanged) {
        this.notifyStore.showSaveSuccessMessage();
      }
      window.location.reload();
    }
    this._saveState = undefined;
  }

  private _onFormValuesChanges(form: any): void {
    if (form['Language']) {
      const warningNotification = this._getWarningNotification();
      const language = form['Language'] as ILanguage;
      if (!warningNotification && language.Key !== this.languageStore?.SelectedLanguage?.Key) {
        this.notifyStore.addMessage({
          ErrorKey: this._changeLanguageWarningKey,
          Level: ApValidationLevel.Warning,
          UserId: 0,
          UserName: '',
          Parameters: [],
          Counter: 1,
          DisplayTimeout: 0
        });
        return;
      }
      if (warningNotification) {
        this.notifyStore.removeMessage(warningNotification);
      }
    }
  }

  private _getWarningNotification(): IApValidationResult | null {
    return this.notifyStore.getNotify().find(n => n.ErrorKey === this._changeLanguageWarningKey);
  }

  private _isUserDataChanged(userData: IUser | null, formValue: any): boolean {
    return userData?.Forename !== formValue['Forename'] ||
      userData?.Surname !== formValue['Surname'];
  }

  private _isLanguageChanged(selectedLanguage: ILanguage | null, formValue: any): boolean {
    return selectedLanguage?.Key !== formValue['Language']?.Key;
  }
}
